/**
 * THE journey-stage vocabulary of the admin — the one place stage keys map to
 * a label, a tone and a definition. Used by the Users table tag, the user
 * detail panel, the user page "State" pill, the stage filter and the CSV
 * export; nothing else may define stage names.
 *
 * Keys are the server's `journeyStage` values (clockout-service
 * `analytics/journey-stage.js`): the Reports funnel card keys, in funnel
 * order, plus `unknown`. Labels match the funnel cards rendered above the
 * table so the same person reads the same way in both places.
 *
 * `tone` values must match the suffix of the SCSS class `tone_<tone>` in
 * `status-pill-tag.module.scss`.
 */
export const JOURNEY_STAGE_UNKNOWN = 'unknown';

export const JOURNEY_STAGES = Object.freeze({
    tc_pending: {
        label: 'T&C Pending',
        tone: 'neutral',
        description: 'Opted in but no progress at all: never accepted the Terms & Conditions, never opened a payroll link, never fetched a balance.',
    },
    pending_payroll_link: {
        label: 'Pending Payroll Link',
        tone: 'warn',
        description: 'Accepted terms or opened a payroll link, but never completed or failed one.',
    },
    payroll_link_error: {
        label: 'Payroll Link Error',
        tone: 'danger',
        description: 'Last payroll-link outcome was an error or early exit; never linked successfully.',
    },
    verifying_payroll_data: {
        label: 'Verifying Payroll Data',
        tone: 'info',
        description: 'Payroll link succeeded but no payroll balance has been fetched yet and no ownership failure was recorded.',
    },
    invalid_ownership: {
        label: 'Invalid Ownership',
        tone: 'danger',
        description: 'Ownership verification conflicted or the account was unlinked, and the user never enrolled.',
    },
    dds_pending: {
        label: 'DDS Pending',
        tone: 'warn',
        description: 'A direct-deposit switch is awaiting confirmation.',
    },
    enrolled: {
        label: 'Enrolled',
        tone: 'success',
        description: 'Payroll balance has been fetched at least once.',
    },
    first_advance: {
        label: 'First Advance',
        tone: 'primary',
        description: 'Has taken at least one advance.',
    },
    [JOURNEY_STAGE_UNKNOWN]: {
        label: 'Unknown',
        tone: 'neutral',
        description: 'None of the sources can place this user: no advance, no pending direct-deposit switch and no analytics record.',
    },
});

/** Stage keys in funnel order (then `unknown`). */
export const JOURNEY_STAGE_ORDER = Object.freeze(Object.keys(JOURNEY_STAGES));

/**
 * Resolve a stage key to its display entry. A value this build does not
 * recognise is still VISIBLE as "Unknown" — with the raw value in the
 * description so support can report it — never silently rendered as another
 * stage.
 *
 * @param {string | null | undefined} stage
 * @returns {{ key: string, label: string, tone: string, description: string, recognised: boolean }}
 */
export function getJourneyStage(stage) {
    if (stage && Object.prototype.hasOwnProperty.call(JOURNEY_STAGES, stage)) {
        return { key: stage, recognised: true, ...JOURNEY_STAGES[stage] };
    }
    const unknown = JOURNEY_STAGES[JOURNEY_STAGE_UNKNOWN];
    return {
        key: JOURNEY_STAGE_UNKNOWN,
        recognised: stage == null,
        label: unknown.label,
        tone: unknown.tone,
        description: stage == null
            ? 'Stage not provided by the server.'
            : `Unrecognised stage value "${stage}" — this admin build does not know it.`,
    };
}

/** Plain label for CSV exports and text rows. */
export function journeyStageLabel(stage) {
    return getJourneyStage(stage).label;
}

/** Filter chip options for the Users report ("Stage: All" first). */
export const JOURNEY_STAGE_FILTER_OPTIONS = Object.freeze([
    { value: 'all', label: 'Stage: All' },
    ...JOURNEY_STAGE_ORDER.map((key) => ({ value: key, label: JOURNEY_STAGES[key].label })),
]);
